import {
  ArrowPathIcon,
  Bars3BottomLeftIcon,
  CheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { PropsWithChildren, useState } from "react";

import { Badge, Small, Tooltip } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import { SuggestionIndicator } from "@components/QaReview/components/SuggestionIndicator";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";

const getFileIcon = (fileName: string) => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  const videoExtensions = ["mp4", "mov", "avi", "webm", "mkv", "m4v"];

  if (extension && videoExtensions.includes(extension)) {
    return <VideoCameraIcon className="h-4 w-4 text-blurple-500 shrink-0" />;
  }
  return <DocumentTextIcon className="h-4 w-4 text-blurple-500 shrink-0" />;
};

export const AiSourcesTooltipContent: React.FC<{ sources: string; useListIcon?: boolean }> = ({
  sources,
  useListIcon = false,
}) => {
  const fileNames = sources
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-limestone-400">Generation based on</span>
      <div className="flex flex-col gap-1.5">
        {fileNames.map((fileName, index) => (
          <div key={index} className="flex items-center gap-2">
            {useListIcon ? (
              <Bars3BottomLeftIcon className="h-4 w-4 text-blurple-500 shrink-0" />
            ) : (
              getFileIcon(fileName)
            )}
            <span className="text-sm text-white ph-no-capture">{fileName}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
export interface FieldComponentProps extends PropsWithChildren {
  id: string;
  title?: string | null;
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
  };
  placeholder: string;
  isDeleting?: boolean;
  onEditTitle: (newTitle: string) => Promise<unknown>;
  actionButtonMenu?: PopoverMenuItem[];
  isContentEditOnly?: boolean;
  disableTitleEdit?: boolean;
  suggestionCount?: number;
  isAi?: boolean;
  aiSources?: string | null;
  /** Override text for AI badge tooltip - when set, always shows the AI badge with this text */
  aiSourcesOverride?: string;
  /** Use list icon instead of file icons in AI sources tooltip */
  useListIconForAiSources?: boolean;
  hasRequiredError?: boolean;
  isRequired?: boolean;
}

export const FieldComponent: React.FC<FieldComponentProps> = ({
  id,
  permissions,
  title,
  placeholder,
  onEditTitle,
  children,
  actionButtonMenu,
  isContentEditOnly = false,
  disableTitleEdit = false,
  suggestionCount = 0,
  isAi,
  aiSources = null,
  aiSourcesOverride,
  useListIconForAiSources = false,
  hasRequiredError = false,
  isRequired = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(title ?? "");
  const [tempSuccessShowing, setTempSuccessShowing] = useState<boolean>(false);
  const [errorShowing, setErrorShowing] = useState<boolean>(false);

  const isReviewPanelVisible = useIsReviewPanelVisible();

  const handleSave = async () => {
    try {
      setTempSuccessShowing(true);
      await onEditTitle(newTitle);
      setTempSuccessShowing(false);
      setIsEditing(false);
    } catch (err) {
      console.error(`Error when saving field title`, err);
      setErrorShowing(true);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTitle(e.target.value);
  };

  return (
    <div
      className={`${!permissions.canEdit ? "mt-4" : ""} rounded-3xl bg-white`}
      id={id}
      key={id}
      onMouseOver={(e) => e.stopPropagation()}
    >
      <div className={`flex items-center justify-between ${isEditing ? "pb-10" : ""}`}>
        <div className="flex items-center relative group w-full" id={id}>
          {isEditing && permissions.canEdit ? (
            <div className="w-full">
              <input
                className="peer block w-full border-0 py-1.5 text-text-primary placeholder-limestone-200 ring-inset ring-limestone-200 placeholder:text-limestone-400 sm:text-sm sm:leading-6 focus:ring-0 focus:outline-hidden cursor-text"
                type="text"
                defaultValue={newTitle}
                placeholder={placeholder}
                onChange={handleTitleChange}
                autoFocus
              />
              <div className="absolute -bottom-8 left-3 flex items-center gap-x-2">
                <XMarkIcon
                  onClick={() => {
                    setErrorShowing(false);
                    setTempSuccessShowing(false);
                    setNewTitle(title ?? "");
                    setIsEditing(false);
                  }}
                  className="h-6 w-6 cursor-pointer rounded-md border border-limestone-200 bg-transparent p-1 text-text-secondary transition-all hover:bg-limestone-50"
                />
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-md border ${
                    tempSuccessShowing
                      ? "border-green-700 bg-green-100"
                      : errorShowing
                        ? "border-red-700 bg-red-100"
                        : "border-limestone-200"
                  }`}
                >
                  {tempSuccessShowing ? (
                    <ArrowPathIcon className="h-6 w-6 animate-spin p-1 text-text-secondary" />
                  ) : (
                    <>
                      {errorShowing ? (
                        <ExclamationTriangleIcon className="h-6 w-6 p-1 text-red-700" />
                      ) : (
                        <CheckIcon
                          onClick={handleSave}
                          className={`h-6 w-6 p-1 cursor-pointer ${
                            tempSuccessShowing ? "text-green-700" : "text-text-secondary"
                          }`}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <Small
                displayType="normal"
                colorType="secondary"
                data-dynamic-field
                className={`${!disableTitleEdit && permissions.canEdit ? "cursor-pointer py-2" : disableTitleEdit || isContentEditOnly ? "py-2" : ""} `}
                onClick={() => !disableTitleEdit && permissions.canEdit && setIsEditing(true)}
              >
                {newTitle}
                {isRequired && (
                  <span aria-label="required" className="ml-0.5 text-red-700">
                    *
                  </span>
                )}
              </Small>
              {!disableTitleEdit && permissions.canEdit ? (
                <PencilIcon
                  onClick={() => setIsEditing(true)}
                  className="h-6 w-6 ml-1 cursor-pointer rounded-md border border-limestone-200 p-1 text-text-secondary opacity-0 group-hover:opacity-100"
                />
              ) : (
                <></>
              )}
            </>
          )}
        </div>
        {hasRequiredError && <Badge text="Required" appearance="error" />}
        {isAi && (aiSources || aiSourcesOverride) && (
          <Tooltip
            text={
              <AiSourcesTooltipContent
                sources={aiSourcesOverride || aiSources!}
                useListIcon={useListIconForAiSources}
              />
            }
            placement="right"
            showInfoIcon={false}
            className="inline-block"
          >
            <span className="inline-flex max-h-6 min-w-fit items-center rounded-md px-1.5 py-0.5 text-xs font-normal cursor-pointer bg-blurple-50 text-blurple-700 mr-[25px]">
              AI
            </span>
          </Tooltip>
        )}
        {isAi && !(aiSources || aiSourcesOverride) && (
          <span className="inline-flex max-h-6 min-w-fit items-center rounded-md px-1.5 py-0.5 text-xs font-normal cursor-pointer bg-blurple-50 text-blurple-700 mr-[25px]">
            AI
          </span>
        )}
        {permissions.canEdit && actionButtonMenu && !isContentEditOnly ? (
          <PopoverMenu openDirection="to-bottom-left" menuItems={actionButtonMenu} />
        ) : (
          <></>
        )}
        {suggestionCount > 0 && isReviewPanelVisible && (
          <SuggestionIndicator fieldId={id} suggestionCount={suggestionCount} className="mr-5" />
        )}
      </div>
      {children}
    </div>
  );
};
